import { combineReducers, configureStore, Middleware } from '@reduxjs/toolkit';
import userReducer, { UserState } from '../features/userSlice';
import { Logger } from '../logger';

const STORAGE_KEY = 'user-state';
const OLD_PERSIST_KEYS = ['persist:root', 'persist:user', 'persist:user-v2'];

// Clean up old Redux persist keys to prevent loading stale data
const cleanupOldPersistKeys = (): void => {
    if (typeof window !== 'undefined' && window.localStorage) {
        OLD_PERSIST_KEYS.forEach((key) => {
            localStorage.removeItem(key);
        });
    }
};

// Load initial state from localStorage
const loadState = (): { user?: UserState } => {
    try {
        if (typeof window === 'undefined') {
            return {};
        }
        const serializedState = localStorage.getItem(STORAGE_KEY);
        if (serializedState === null) {
            return {};
        }
        return { user: JSON.parse(serializedState) };
    } catch (err) {
        console.error('Failed to load state from localStorage:', err);
        return {};
    }
};

// Save state to localStorage
// Returns true if save succeeded, false otherwise
const saveState = (state: { user: UserState }): boolean => {
    try {
        if (typeof window === 'undefined') {
            return false;
        }
        const serializedState = JSON.stringify(state.user);
        localStorage.setItem(STORAGE_KEY, serializedState);
        return true;
    } catch (err) {
        console.error('Failed to save state to localStorage:', err);
        return false;
    }
};

// Actions that should trigger state persistence
const USER_ACTIONS_TO_PERSIST = [
    'user/setCredentialsAndUser',
    'user/setTokens',
    'user/logoutUser',
    'user/getUserDetails/fulfilled',
    'user/updateUser/fulfilled',
] as const;

// Middleware to save state only for specific user-related actions
const localStorageMiddleware: Middleware = (store) => (next) => (action) => {
    const result = next(action);
    if (
        typeof action === 'object' &&
        action !== null &&
        'type' in action &&
        USER_ACTIONS_TO_PERSIST.includes(action.type as (typeof USER_ACTIONS_TO_PERSIST)[number])
    ) {
        const saved = saveState(store.getState() as { user: UserState });
        if (!saved) {
            // State persistence failed - app continues working but won't survive page reload
            // This can happen in private browsing mode or when localStorage quota is exceeded
            const actionType = (action as { type: string }).type;
            Logger.warn(`Failed to persist state for action: ${actionType}`);
        }
    }
    return result;
};

// Root reducer combining all feature reducers
const rootReducer = combineReducers({
    user: userReducer,
});

// Type exports
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

// Clean up old persist keys before loading state (prevents stale data)
cleanupOldPersistKeys();
const preloadedState = loadState();

// Configure and export the main store
export const store = configureStore({
    reducer: rootReducer,
    preloadedState: preloadedState.user ? { user: preloadedState.user } : undefined,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: false,
        }).concat(localStorageMiddleware),
});

// Setup store for testing purposes
export const setupStore = (initialState?: Partial<RootState>): ReturnType<typeof configureStore> =>
    configureStore({
        reducer: rootReducer,
        preloadedState: initialState,
    });
